import {
  EXTENSIONS_SELF_REPORTED,
  EXTENSION_URL,
} from '@constants/extension-codes';
import { VACCINE_CODE_MAP } from '@constants/option-types/immunization';

import {
  ImmunizationRecord,
  type ImmunizationValues,
} from '@models/immunization';

import { formatPatientId } from '@utils/format';

import type { ImmunizationModelInput } from 'generated/types';
import { ImmunizationStatus } from 'generated/types';
import type { Extension } from 'generated/types';

export function getImmunizationTemplate(
  patient_id: string,
  values: ImmunizationValues
): ImmunizationModelInput {
  const extension: Extension[] = [];
  if (values.associatedResources) {
    values.associatedResources.forEach((resource) => {
      extension.push({
        url: 'https://vitall.com/documentation/extensions/associated-records',
        valueReference: {
          reference: resource,
        },
      });
    });
  }
  if (values.selfReported) {
    extension.push({
      url: `${EXTENSION_URL}${EXTENSIONS_SELF_REPORTED}`,
      valueBoolean: values.selfReported,
    });
  }

  return {
    resourceType: 'Immunization',
    ...(values.expirationDate && {
      expirationDate: values.expirationDate,
    }),
    ...(values.lotNumber && {
      lotNumber: values.lotNumber,
    }),
    ...(values.brand && {
      manufacturer: {
        display: values.brand,
      },
    }),
    ...(values.immunizationDate && {
      occurrenceDateTime: values.immunizationDate,
    }),
    patient: {
      reference: formatPatientId(patient_id),
    },
    ...(values.practitioner && {
      performer: [
        {
          actor: { display: values.practitioner },
        },
      ],
    }),
    ...(values.dose && {
      protocolApplied: [
        {
          doseNumberString: values.dose,
        },
      ],
    }),
    reasonCode: [
      {
        text: values.standard ? 'Standard' : 'Travel',
        coding: [
          {
            code: values.standard ? '429060002' : '281657000',
            display: values.standard ? 'Standard' : 'Travel',
            system: 'http://snomed.info/sct',
          },
        ],
      },
    ],
    ...(values.route && {
      route: {
        text: values.route,
      },
    }),
    status: ImmunizationStatus.notDone,
    vaccineCode: {
      text: values.title,
      ...(VACCINE_CODE_MAP[values.title] && {
        coding: [
          {
            code: VACCINE_CODE_MAP[values.title],
            display: values.title,
            system: ImmunizationRecord.VITALL_SYSTEM,
          },
        ],
      }),
    },
    ...(extension.length && {
      extension,
    }),
  };
}
